/**
 * @module components/PackageView
 * @description Complete package view component for order history.
 * Displays package status, download button, password copy, regenerate functionality, and polling for Aspera transfers.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FaKey } from 'react-icons/fa';
import { IoMdDownload } from 'react-icons/io';
import { apiCallAsync } from 'src/api/apiCall';
import { AssetPackageStatus } from 'src/components/AssetPackageStatus';
import { PackageButton } from 'src/components/PackageButton';
import { StatusSpinner } from 'src/components/StatusSpinner';
import { Toast } from 'src/components/Toast';
import { ToastDuration } from 'src/components/Toast/types';
import { Tooltip } from 'src/components/Tooltip';
import { usePrevious } from 'src/modules/usePrevious';
import PackageStatusPoller from 'src/pages/Order/PackageStatusPoller';
import { useOrders } from 'src/providers/OrdersProvider';
import { Assets } from 'src/types/assets/types';
import { Sanity } from 'src/types/sanity';
import { copyPassword, hasExpired, openLink, randSelector } from 'src/utils';
import * as Styles from './styles';
import { PackageViewProps } from './types';

export const PASSWORD_COPY_TIMEOUT = 1000;

/**
 * @component PackageView
 * @summary Complete package display with status, actions, and transfer polling.
 * Handles download, password copy, regeneration, and real-time Aspera transfer status updates.
 * Shows appropriate actions based on package state (active/expired/failed).
 * @type {React.FC<PackageViewProps>}
 * @param {PackageViewProps} props - Component props from types.ts
 * @returns {React.ReactElement|null} Package view with status and actions or null if no package
 */
export const PackageView: React.FC<PackageViewProps> = ({
  assetPackage,
  orderId,
  index,
  totalCount = 1,
  downloadLink,
  newDownloadLinkRequested,
  onReplaceOrder,
  className,
}) => {
  const { refreshOrder } = useOrders();
  const [isWorking, setIsWorking] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleRegenerate = useCallback(async () => {
    if (!assetPackage) return;
    setIsWorking(true);

    Toast.info('Starting package regeneration...');

    try {
      await apiCallAsync(`assets?orderId=${orderId}${assetPackage._key ? `&packageId=${assetPackage._key}` : ''}`);
      try {
        const fresh = await refreshOrder(orderId);
        if (fresh) {
          onReplaceOrder?.(fresh);
        }
      } catch { /* ignore UI refresh errors */ }
    } finally {
      setIsWorking(false);
    }
  }, [assetPackage, onReplaceOrder, orderId, refreshOrder]);

  const handleCopyPassword = useCallback(() => {
    if (!assetPackage?.linkPassword || passwordCopied) return;
    copyPassword(assetPackage.linkPassword)()
      .then(() => {
        Toast.success('Password copied to clipboard');
        setPasswordCopied(true);
        setTimeout(() => {
          setPasswordCopied(false);
        }, PASSWORD_COPY_TIMEOUT);
      })
      .catch(() => {
        Toast.error('Failed to copy password');
      });
  }, [assetPackage, passwordCopied]);

  const handleOpenLink = useCallback(() => {
    if (!assetPackage) return;
    openLink(assetPackage.link)();
  }, [assetPackage]);

  // Hooks and derived state must run before any early return
  const isAspera = assetPackage?.assetProvider === Assets.Types.Providers.ASPERA;

  const isRegenerating = isWorking || (
    isAspera && (
      assetPackage?.transferStatus !== Assets.Types.TransferStatus.FAILED && (
        assetPackage?.transferStatus === Assets.Types.TransferStatus.RUNNING ||
        assetPackage?.transferStatus === Assets.Types.TransferStatus.WAITING ||
        assetPackage?.transferStatus === Assets.Types.TransferStatus.PAUSED ||
        assetPackage?.transferStatus === Assets.Types.TransferStatus.STARTING
      )
    )
  );

  const prevIsRegenerating = usePrevious(isRegenerating) ?? false;

  // useEffect(() => {
  //   if (!prevIsRegenerating && isRegenerating) {
  //     Toast.info('Starting package regeneration...');
  //   }
  // }, [isRegenerating, prevIsRegenerating]);

  useEffect(() => {
    if (prevIsRegenerating && !isRegenerating) {
      const failed = isAspera && (
        assetPackage?.transferStatus === Assets.Types.TransferStatus.FAILED ||
        assetPackage?.transferStatus === Assets.Types.TransferStatus.CANCELED ||
        assetPackage?.transferStatus === Assets.Types.TransferStatus.PARTIALLY_COMPLETED
      );
      if (failed || assetPackage?.transferError) {
        const err = assetPackage?.transferError || 'Unknown error';
        Toast.error(`Package generation failed - ${err}`, {
          autoClose: undefined,
        });
      } else {
        Toast.success('Package ready!', {
          autoClose: ToastDuration.LONG,
        });
      }
    }
  }, [isRegenerating, prevIsRegenerating, isAspera, assetPackage]);

  // Legacy download-only UI when there are no packages
  if (!assetPackage) {
    if (downloadLink) {
      return (
        <Styles.Container className={className}>
          <PackageButton
            key={'package-legacy-download'}
            tone={Sanity.Types.SanityTone.POSITIVE}
            onClick={openLink(downloadLink)}
            icon={<IoMdDownload />}
          >
            Download {/* totalCount > 1 ? `#${(index ?? 0) + 1}` : '' */}
          </PackageButton>
        </Styles.Container>
      );
    }
    if (newDownloadLinkRequested) {
      return <p className={className}>New DownloadLink Requested</p>;
    }
    return null;
  }

  const parts: React.ReactNode[] = [];
  const actions: React.ReactNode[] = [];

  if (
    isRegenerating ||
    (isAspera &&
      (assetPackage?.transferStatus === Assets.Types.TransferStatus.FAILED ||
        assetPackage?.transferStatus === Assets.Types.TransferStatus.CANCELED ||
        assetPackage?.transferStatus === Assets.Types.TransferStatus.PARTIALLY_COMPLETED)) ||
    hasExpired(assetPackage)
  ) {
    actions.push(
      <PackageButton
        key={`package-${assetPackage._key}-regenerate`}
        onClick={handleRegenerate}
        disabled={isRegenerating}
        tone={Sanity.Types.SanityTone.CAUTION}
        icon={<StatusSpinner loading={isRegenerating} />}
      >
        {isRegenerating ? 'Processing...' : 'Regenerate'}
      </PackageButton>,
    );
  } else {
    if (assetPackage.linkPassword) {
      const id = randSelector('package-button');
      actions.push(
        <PackageButton
          key={`package-${assetPackage._key}-password`}
          tone={Sanity.Types.SanityTone.CAUTION}
          onClick={handleCopyPassword}
          disabled={passwordCopied}
          icon={<FaKey />}
          data-tooltip-id={id}
          data-tooltip-content={assetPackage.linkPassword}
        >
          {passwordCopied ? 'Password Copied!' : 'Copy Password'}
        </PackageButton>,
        <Tooltip key={id} id={id} style={{ fontFamily: 'monospace, inherit' }} />,
      );
    }
    actions.push(
      <PackageButton
        key={`package-${assetPackage._key}`}
        tone={Sanity.Types.SanityTone.POSITIVE}
        onClick={handleOpenLink}
        icon={<IoMdDownload />}
      >
        Download {/* totalCount > 1 ? `#${(index ?? 0) + 1}` : '' */}
      </PackageButton>,
    );
  }

  parts.push(<AssetPackageStatus key={`package-status-${assetPackage._key}`} assetPackage={assetPackage} />);

  // Headless poller for running transfers (Aspera only)
  if (
    isAspera &&
    (assetPackage.transferStatus === Assets.Types.TransferStatus.RUNNING ||
      assetPackage.transferStatus === Assets.Types.TransferStatus.WAITING ||
      assetPackage.transferStatus === Assets.Types.TransferStatus.PAUSED)
  ) {
    parts.push(
      <PackageStatusPoller
        key={`poller-${assetPackage._key}`}
        orderId={orderId}
        pkg={assetPackage}
        onReplaceOrder={onReplaceOrder}
      />,
    );
  }

  // if (isAspera && assetPackage.transferStatus) {
  //   parts.push(
  //     <p key={`package-${assetPackage._key}-transfer-status`} style={{ margin: '0' }}>Status: {assetPackage.transferStatus}</p>,
  //   );
  // }

  // if (isAspera && assetPackage.transferError) {
  //   parts.push(<p key={`package-${assetPackage._key}-transfer-error`}>Transfer error: {assetPackage.transferError}</p>);
  // }

  parts.push(
    <Styles.Actions key={`package-${assetPackage._key}-actions`}>
      {actions}
    </Styles.Actions>,
  );

  return (
    <Styles.Container className={className}>
      {parts}
    </Styles.Container>
  );
};

export default PackageView;
